import type { RuleAction, RuleView } from "@kwp/contracts";

/**
 * Settings → Rules, without React (architecture §7): the action builder's rows, the scope picker's
 * choice, and a rule prefilled from an email ("Make a rule from this").
 */
export type ActionKind = RuleAction["kind"];

export const ACTION_CHOICES: ReadonlyArray<{ kind: ActionKind; label: string; needs: "tag" | "folder" | "address" | null }> = [
  { kind: "archive", label: "Archive", needs: null },
  { kind: "markRead", label: "Mark read", needs: null },
  { kind: "star", label: "Star", needs: null },
  { kind: "tag", label: "Tag", needs: "tag" },
  { kind: "moveTo", label: "Move to folder", needs: "folder" },
  { kind: "spam", label: "Mark spam", needs: null },
  { kind: "trash", label: "Move to Trash", needs: null },
  { kind: "forward", label: "Forward to", needs: "address" },
];

/** One row of the actions builder: a kind and, for tag / move / forward, its value. */
export interface ActionDraft {
  kind: ActionKind;
  value: string;
}

export function needsValue(kind: ActionKind): "tag" | "folder" | "address" | null {
  const choice = ACTION_CHOICES.find((c) => c.kind === kind);
  return choice === undefined ? null : choice.needs;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The rows as rule actions, or the first problem in words. */
export function toActions(drafts: readonly ActionDraft[]): { actions: RuleAction[] } | { error: string } {
  if (drafts.length === 0) return { error: "Add at least one action." };
  const actions: RuleAction[] = [];
  for (const d of drafts) {
    const value = d.value.trim();
    const needs = needsValue(d.kind);
    if (needs !== null && value.length === 0) return { error: `Enter the ${needs === "address" ? "address to forward to" : `${needs} name`}.` };
    switch (d.kind) {
      case "tag":
        actions.push({ kind: "tag", tag: value });
        break;
      case "moveTo":
        actions.push({ kind: "moveTo", folder: value });
        break;
      case "forward":
        if (!EMAIL.test(value)) return { error: `${value} is not an email address.` };
        actions.push({ kind: "forward", to: value });
        break;
      default:
        actions.push({ kind: d.kind });
    }
  }
  return { actions };
}

export function toDrafts(actions: readonly RuleAction[]): ActionDraft[] {
  return actions.map((a) => {
    switch (a.kind) {
      case "tag":
        return { kind: a.kind, value: a.tag };
      case "moveTo":
        return { kind: a.kind, value: a.folder };
      case "forward":
        return { kind: a.kind, value: a.to };
      default:
        return { kind: a.kind, value: "" };
    }
  });
}

/** Trash and forward rules need a passkey (or approval) to be saved (§7, §9). */
export function isDestructiveDraft(drafts: readonly ActionDraft[]): boolean {
  return drafts.some((d) => d.kind === "trash" || d.kind === "forward");
}

export function describeAction(a: RuleAction): string {
  switch (a.kind) {
    case "tag":
      return `Tag “${a.tag}”`;
    case "moveTo":
      return `Move to “${a.folder}”`;
    case "forward":
      return `Forward to ${a.to}`;
    default: {
      const choice = ACTION_CHOICES.find((c) => c.kind === a.kind);
      return choice === undefined ? a.kind : choice.label;
    }
  }
}

/** Which accounts the rule covers: all of them, a workspace's, or picked ones. */
export type ScopeChoice = { kind: "all" } | { kind: "workspace"; workspaceId: string } | { kind: "accounts"; accountIds: string[] };

export function scopeInput(scope: ScopeChoice): { workspaceId?: string; accountIds?: string[] } {
  if (scope.kind === "workspace") return { workspaceId: scope.workspaceId };
  if (scope.kind === "accounts") return { accountIds: scope.accountIds };
  return {};
}

export function scopeOf(rule: Pick<RuleView, "workspaceId" | "accountIds">, allAccountIds: readonly string[]): ScopeChoice {
  if (rule.workspaceId !== null) return { kind: "workspace", workspaceId: rule.workspaceId };
  const all = allAccountIds.length === rule.accountIds.length && allAccountIds.every((id) => rule.accountIds.includes(id));
  return all ? { kind: "all" } : { kind: "accounts", accountIds: [...rule.accountIds] };
}

/** What the dialog starts from: a new rule, an existing one, or one made from an email. */
export interface RuleDraft {
  ruleId: string | null;
  name: string;
  query: string;
  actions: ActionDraft[];
  scope: ScopeChoice;
}

export const EMPTY_DRAFT: RuleDraft = { ruleId: null, name: "", query: "", actions: [{ kind: "archive", value: "" }], scope: { kind: "all" } };

/** The fields of an email a rule can be made from. */
export interface RuleSourceMessage {
  from: { name: string | null; email: string };
  accountId: string;
}

const quoteIfNeeded = (v: string) => (/[\s"()]/.test(v) ? `"${v.replace(/"/g, "")}"` : v);

/** "Make a rule from this": mail from the same sender, on the account it came to. */
export function draftFromMessage(m: RuleSourceMessage): RuleDraft {
  const sender = m.from.email.toLowerCase();
  return {
    ruleId: null,
    name: m.from.name === null ? `Mail from ${sender}` : `Mail from ${m.from.name}`,
    query: `from:${quoteIfNeeded(sender)}`,
    actions: [{ kind: "archive", value: "" }],
    scope: { kind: "accounts", accountIds: [m.accountId] },
  };
}

export function draftFromRule(rule: RuleView, allAccountIds: readonly string[]): RuleDraft {
  return { ruleId: rule.id, name: rule.name, query: rule.query, actions: toDrafts(rule.actions), scope: scopeOf(rule, allAccountIds) };
}
